import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Select } from "@opendate-app/setlist";
import PromoCodeForm from "../../ConfirmTicketing/components/PromoCodeForm";
import { blankPromoCode, PROMO_CODE_NAME_FORMAT } from "../scheduleBuilder";
import { formToPromo, promoToForm } from "../confirmAdapters";
import ShowDetailOffset, { offsetsWithinAYear } from "./ShowDetailOffset";

// The confirm's rules for a default promo code, plus the series' own: a
// template has no offset (PromoCodes::SeriesPlan#default_template_problem).
export const defaultPromoCodeProblem = (code) => {
  if (!code.startActivity || !code.endActivity) return "A default promo code has to open and close on show details.";
  if (Number(code.startOffsetMinutes) || Number(code.endOffsetMinutes))
    return "A default promo code has to open and close right at its show details, with no offset.";
  if (code.revealHiddenTickets && !code.revealAllHiddenTickets)
    return "A default promo code that reveals hidden tickets has to reveal all of them.";
  if (code.discountsOnlySelectedTicketTypes)
    return "A default promo code has to discount every ticket, not only selected ones.";
  return null;
};

export const promoCodeProblems = (code, { takenNames = [], showsWindow = true }) => {
  const name = code.name.trim().toUpperCase();
  return {
    name: !PROMO_CODE_NAME_FORMAT.test(name) ? "format" : takenNames.includes(name) ? "Another promo code already uses this name." : null,
    discount: (code.discount === "" && !code.revealHiddenTickets) || (code.discountType === "percentage" && Number(code.discount) > 100) ? "discount" : null,
    limit: !code.unlimited && code.limit === "" ? "limit" : null,
    window: showsWindow && !(code.startActivity && code.endActivity) ? "window" : null,
    offsets: offsetsWithinAYear(code.startOffsetMinutes, code.endOffsetMinutes) ? null : "offsets",
    discounted: code.appliesToTicketTypes && code.discountsOnlySelectedTicketTypes && (code.discountedTicketTypeNames || []).length === 0 ? "discounted" : null,
    default: code.shouldBeDefault ? defaultPromoCodeProblem(code) : null,
  };
};

const PromoCodeDrawer = ({ open, initial, editing, takenNames, ticketTypeNames, holdNames = [], showActivityNames, onCancel, onSave }) => {
  const showsWindow = showActivityNames.length > 0;
  const base = useMemo(() => ({ ...blankPromoCode(), ...(initial || {}) }), [initial]);
  const initialForm = useMemo(() => promoToForm(base, { editing: editing ?? Boolean(initial) }), [base, editing]);
  const activityOptions = showActivityNames.map((activity) => ({ value: activity, label: activity }));
  const problemsFor = (form) => promoCodeProblems(formToPromo(form, base), { takenNames, showsWindow });

  const renderWindow = (form, setForm) => {
    const set = (changes) => setForm({ ...form, ...changes });
    return (
      <>
        <p className="ticket-drawer-group-title">When are promo codes available?</p>
        {!showsWindow && <p className="ticket-drawer-muted-text">From the public on-sale until each date&apos;s last entry ends.</p>}
        {showsWindow && (
          <>
            <Select
              id="promo-code-start-show-activity"
              label="Start Show Detail"
              placeholder="Select a show detail"
              options={activityOptions}
              value={form.startActivity || null}
              onChange={(value) => set({ startActivity: value || "", startOffsetMinutes: 0 })}
            />
            {form.startActivity && (
              <ShowDetailOffset label="Start Show Detail" anchorName={form.startActivity} minutes={form.startOffsetMinutes} onChange={(minutes) => set({ startOffsetMinutes: minutes })} />
            )}
            <Select
              id="promo-code-end-show-activity"
              label="End Show Detail"
              placeholder="Select a show detail"
              options={activityOptions}
              value={form.endActivity || null}
              onChange={(value) => set({ endActivity: value || "", endOffsetMinutes: 0 })}
            />
            {form.endActivity && (
              <ShowDetailOffset label="End Show Detail" anchorName={form.endActivity} minutes={form.endOffsetMinutes} onChange={(minutes) => set({ endOffsetMinutes: minutes })} />
            )}
          </>
        )}
        <p className="ticket-drawer-muted-text">Each date in the series gets its own copy of this code, with its own uses.</p>
      </>
    );
  };

  return (
    <PromoCodeForm
      isOpen={open}
      setIsOpen={onCancel}
      onClose={onCancel}
      currentUser={{}}
      confirm={{}}
      csrfToken=""
      team={{}}
      promoCodeToCopy={{}}
      promoCodeToEdit={{}}
      showActivities={[]}
      ticketTypes={ticketTypeNames.map((name) => ({ id: name, name }))}
      holds={holdNames.map((name) => ({ id: name, name }))}
      initialPromoCode={initialForm}
      canSubmit={(form) => Object.values(problemsFor(form)).every((problem) => !problem)}
      nameError={(form) => {
        const problem = problemsFor(form).name;
        return problem && problem !== "format" ? problem : null;
      }}
      saveAsDefaultProblem={(form) => problemsFor(form).default}
      renderWindow={renderWindow}
      onSubmitPromoCode={(form) => {
        const code = formToPromo(form, base);
        onSave({
          ...code,
          startActivity: showsWindow ? code.startActivity : null,
          endActivity: showsWindow ? code.endActivity : null,
        });
      }}
    />
  );
};

PromoCodeDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  initial: PropTypes.object,
  editing: PropTypes.bool,
  takenNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  ticketTypeNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  holdNames: PropTypes.arrayOf(PropTypes.string),
  showActivityNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default PromoCodeDrawer;
